import { LAYOUT_COLUMNS, keyboardMetrics } from './cssVars';
import { wideGapLayout } from './layouts';
import type { Settings } from '../types';

/**
 * Low-effort mode (3.7): one toggle for a bad day.
 *
 * Pure, so the property that actually matters can be tested — **enabling and exiting loses nothing**, which is
 * 3.7's own acceptance criterion and the only reason a person would ever dare use it. A mode that quietly
 * discarded a caregiver's careful key-size work would be used once.
 *
 * ── Why the snapshot lives in `Settings` and not in `meta` (C61) ──
 *
 * `meta` is excluded from every export by design (ADR-019). So a device restored *while in low-effort mode*
 * would come back with the low-effort values and no memory of what preceded them — the person's real settings,
 * gone silently, with nothing to indicate they ever existed. The snapshot is person data and belongs in the
 * backup.
 *
 * ── Why "largest key size" is not `keyMm: 24` (C66) ──
 *
 * 3.7 asks for the largest key size, and C4 and C31 already established that `keyMm` binds at **19.27 mm** at
 * eleven columns, with the 19 mm default sitting 0.27 mm below it. Setting the slider to 24 would change a
 * number and nothing on screen: the worst kind of feature, one that looks like help.
 *
 * What the ten-column layout actually buys is **gap** — 3.4 mm instead of 1.5 mm, at a cost of 0.35 mm of size —
 * and the design credits gap with preventing mis-hits as much as size does. So the lever is the layout, not the
 * slider. It costs comma, period and hyphen, which on a phrase-first path is a fair trade.
 *
 * ── Why the layout is looked up rather than named ──
 *
 * It is the *nearest* ten-column layout, via `wideGapLayout`, not `de-CH-10col` by name. Widening the gaps must
 * not move the letters: a person on the alphabetical board who tapped `Leichter` used to get QWERTZ, so the one
 * tap meant for a bad day relearned the whole keyboard. Fatigue is exactly when nobody has that to spend.
 */

/** Which fields the mode overrides. Everything else is left exactly as the person had it. */
export const LOW_EFFORT_FIELDS = [
  'keyboardLayout',
  'keyMm',
  'dwellMs',
  'candidateCount',
] as const satisfies ReadonlyArray<keyof Settings>;

export type LowEffortField = (typeof LOW_EFFORT_FIELDS)[number];

/** Longest dwell the contract allows (400–2000). */
const LONGEST_DWELL_MS = 2000;
/** 3.7: "candidate count 3". Fewer things to read is the point. */
const LOW_EFFORT_CANDIDATES = 3;

export interface Viewport {
  widthPx: number;
  keyboardHeightPx: number;
}

/**
 * The values low-effort mode imposes.
 *
 * `keyMm` comes from the layout's own ceiling rather than from a constant, so it is the largest size that layout
 * can *honour* — see C66. Asking for more than the geometry allows is how you get a setting that does nothing.
 */
export function lowEffortValues(current: Settings, viewport: Viewport): Partial<Settings> {
  const layout = wideGapLayout(current.keyboardLayout);
  const metrics = keyboardMetrics({
    keyMm: 99, // Deliberately unreachable: what comes back is the ceiling, which is what we want.
    keyGapMm: current.keyGapMm,
    pxPerMm: current.pxPerMm,
    availableWidthPx: viewport.widthPx,
    availableHeightPx: viewport.keyboardHeightPx,
    columns: LAYOUT_COLUMNS[layout],
    rows: 3,
  });

  return {
    keyboardLayout: layout,
    // Rounded down to the slider's step so care mode shows a value it could itself produce.
    keyMm: Math.floor(metrics.maxKeyMm * 2) / 2,
    dwellMs: LONGEST_DWELL_MS,
    candidateCount: LOW_EFFORT_CANDIDATES,
  };
}

/** The patch that turns the mode on, snapshot included. */
export function enterLowEffort(current: Settings, viewport: Viewport): Partial<Settings> {
  const values = lowEffortValues(current, viewport);

  const restore: Partial<Settings> = {};
  for (const field of LOW_EFFORT_FIELDS) {
    // `as never` only to satisfy the write into a heterogeneous partial; the read is typed.
    (restore as Record<string, unknown>)[field] = current[field];
  }

  return { ...values, lowEffortMode: true, lowEffortRestore: restore };
}

/**
 * The patch that turns it off again.
 *
 * **Only fields still holding the low-effort value are restored.** If a caregiver deliberately changed the key
 * size while the mode was on, that was a decision about how the device should be — and exiting a fatigue mode is
 * no reason to undo it. Restoring wholesale would mean their change vanished at a moment nobody was watching for
 * it.
 */
export function exitLowEffort(current: Settings, viewport: Viewport): Partial<Settings> {
  const restore = current.lowEffortRestore ?? {};
  const imposed = lowEffortValues(current, viewport);

  const patch: Partial<Settings> = { lowEffortMode: false, lowEffortRestore: null };

  for (const field of LOW_EFFORT_FIELDS) {
    if (!(field in restore)) continue;
    // Untouched since we set it? Then it is ours to give back.
    if (current[field] !== imposed[field]) continue;
    (patch as Record<string, unknown>)[field] = restore[field];
  }

  return patch;
}
